import type {
  AttentionPointCriticite,
  AttentionPointStatus,
  AttentionPointType,
  JournalEntryKind,
} from './useAttentionPointsStore'

export interface MetaItem<T extends string> {
  value: T
  label: string
  color: string
}

export const ATTENTION_STATUSES: MetaItem<AttentionPointStatus>[] = [
  { value: 'OUVERT', label: 'OUVERT', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  { value: 'EN_COURS', label: 'EN COURS', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'LEVE', label: 'LEVÉ', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
]

export const ATTENTION_CRITICITES: MetaItem<AttentionPointCriticite>[] = [
  { value: 'FAIBLE', label: 'Faible', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  { value: 'MOYENNE', label: 'Moyenne', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { value: 'ELEVEE', label: 'Élevée', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  { value: 'CRITIQUE', label: 'Critique', color: 'bg-red-100 text-red-800 border-red-200' },
]

export interface AttentionTypeMeta extends MetaItem<AttentionPointType> {
  /** Question guidée pour la description (« le sujet précisément »). */
  descriptionPrompt: string
  /** Question guidée pour l'impact / l'action attendue. */
  impactPrompt: string
}

/**
 * Chaque type correspond à un groupe de questions posées par la hiérarchie.
 * Les prompts servent de placeholders dans le formulaire pour garder la trace.
 */
export const ATTENTION_TYPES: AttentionTypeMeta[] = [
  {
    value: 'BLOCAGE',
    label: 'Blocage',
    color: 'bg-red-100 text-red-800 border-red-200',
    descriptionPrompt: 'Quel est le point de blocage ?',
    impactPrompt: 'Quel impact sur le travail en cours / futur ? Quand est prévue la résolution ?',
  },
  {
    value: 'SUJET_SENSIBLE',
    label: 'Sujet sensible',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    descriptionPrompt: 'Qu’est-ce qui est sensible ?',
    impactPrompt: 'Que devons-nous faire ?',
  },
  {
    value: 'ETUDE_FAISABILITE',
    label: 'Étude de faisabilité',
    color: 'bg-violet-100 text-violet-800 border-violet-200',
    descriptionPrompt: 'Que devons-nous faire ? Peux-tu préciser ?',
    impactPrompt: 'Est-ce une étude de faisabilité ? Quel périmètre ?',
  },
  {
    value: 'SUJET_TRANSVERSE',
    label: 'Sujet transverse',
    color: 'bg-sky-100 text-sky-800 border-sky-200',
    descriptionPrompt: 'Quel est le sujet précisément ?',
    impactPrompt: 'Quelles sont les actions nécessaires ?',
  },
  {
    value: 'AUTRE',
    label: 'Autre',
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    descriptionPrompt: 'Décris le sujet précisément.',
    impactPrompt: 'Quel impact et quelles actions nécessaires ?',
  },
]

export const JOURNAL_KINDS: MetaItem<JournalEntryKind>[] = [
  { value: 'COMMENTAIRE', label: 'Commentaire', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  { value: 'DETAIL', label: 'Détail', color: 'bg-sky-100 text-sky-800 border-sky-200' },
  { value: 'ACTION', label: 'Action effectuée', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
]

/** Seuils (jours) sans activité avant marquage « sans suivi ». Doit suivre le backend. */
export const ATTENTION_STALE_THRESHOLDS = [3, 7, 10, 14, 30] as const
export const DEFAULT_STALE_THRESHOLD_DAYS = 7

/** Domaines suggérés (saisie libre possible en plus). */
export const ATTENTION_DOMAINES_SUGGESTED = [
  'Support technique',
  'Service Client',
  'Facturation',
  'Commercial',
  'Données / Migration',
  'Légal',
  'Organisationnel',
]

export const ATTENTION_STATUS_BY_VALUE = Object.fromEntries(
  ATTENTION_STATUSES.map((s) => [s.value, s]),
) as Record<AttentionPointStatus, MetaItem<AttentionPointStatus>>

export const ATTENTION_CRITICITE_BY_VALUE = Object.fromEntries(
  ATTENTION_CRITICITES.map((s) => [s.value, s]),
) as Record<AttentionPointCriticite, MetaItem<AttentionPointCriticite>>

export const ATTENTION_TYPE_BY_VALUE = Object.fromEntries(
  ATTENTION_TYPES.map((s) => [s.value, s]),
) as Record<AttentionPointType, AttentionTypeMeta>

export const JOURNAL_KIND_BY_VALUE = Object.fromEntries(
  JOURNAL_KINDS.map((s) => [s.value, s]),
) as Record<JournalEntryKind, MetaItem<JournalEntryKind>>
